import React, { useEffect, useRef, useReducer, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { reportRegistration } from '../lib/pixel';
import { takeReturnTo } from '../lib/returnTo';
import { Card, CardContent } from '../components/ui/card';

// State reducer for auth callback
const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, status: action.payload };
    case 'SET_REDIRECT':
      return { ...state, redirect: action.payload, delay: action.delay || 1500 };
    default:
      return state;
  }
};

// CRITICAL: Safety timeout - if auth callback takes too long, redirect to sign-up
const SAFETY_TIMEOUT_MS = 15000;

/**
 * AuthCallback handles:
 * 1. Email verification links (existing flow)
 * 2. Social login redirects (Google, Facebook)
 * 
 * After social login, checks if user needs role selection (progressive profiling)
 */
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const [state, dispatch] = useReducer(authReducer, {
    status: '🔐 Verifying your account...',
    redirect: null,
    delay: 1500
  });

  const handleSessionSuccess = useCallback(async (session) => {
    try {
      console.log('[AuthCallback] Session established for:', session.user.email);
      console.log('[AuthCallback] Provider:', session.user.app_metadata?.provider);

      // Warn if social login returned no email (e.g., phone-only Facebook account)
      const authProvider = session.user.app_metadata?.provider;
      if ((authProvider === 'facebook' || authProvider === 'google') && !session.user.email) {
        console.warn(`[AuthCallback] Social login (${authProvider}) returned no email address. User may have a phone-only account.`);
      }

      // Check if user has a profile with a role set
      // Use auth_user_id to find profile (not id, which is the profile's own UUID)
      // Use maybeSingle to handle edge case where profile doesn't exist yet
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, role, name, town, phone, years_of_experience, education_level, date_of_birth, skills, business_name, industry, number_of_employees, referred_by, registration_source, created_at')
        .eq('auth_user_id', session.user.id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('[AuthCallback] Error fetching profile:', profileError);
      }

      // Apply referral code from URL param (passed through OAuth redirectTo) or localStorage
      // URL param is the primary source since localStorage can be cleared by Safari ITP during OAuth
      try {
        const urlRef = new URLSearchParams(window.location.search).get('ref');
        const storedRefCode = urlRef || localStorage.getItem('referral_code');
        if (storedRefCode && profile?.id && !profile.referred_by) {
          console.log('[AuthCallback] Applying referral code to profile:', storedRefCode, urlRef ? '(from URL)' : '(from localStorage)');
          const { error: refError } = await supabase
            .from('profiles')
            .update({ referred_by: storedRefCode.toUpperCase() })
            .eq('id', profile.id);
          if (!refError) {
            console.log('[AuthCallback] Referral code applied successfully');
            localStorage.removeItem('referral_code');
          } else {
            console.error('[AuthCallback] Error applying referral code:', refError);
          }
        }
      } catch (refErr) {
        console.error('[AuthCallback] Error in referral code application:', refErr);
      }

      // Determine if this is a social login (Google, Facebook)
      const provider = session.user.app_metadata?.provider;
      const isSocialLogin = provider === 'google' || provider === 'facebook';

      // Check if user has role data in user_metadata (from email signup form)
      const userMeta = session.user.user_metadata || {};
      const metaRole = userMeta.role;
      const metaTown = userMeta.town;
      const metaName = userMeta.name;

      // If profile role is pending but user_metadata has a role, update the profile
      if ((!profile?.role || profile.role === 'pending') && metaRole && metaRole !== 'pending') {
        console.log('[AuthCallback] Found role in user_metadata, updating profile:', metaRole);

        // Get profile ID
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id')
          .eq('auth_user_id', session.user.id)
          .maybeSingle();

        if (profileData?.id) {
          const updateData = {
            role: metaRole,
            updated_at: new Date().toISOString()
          };
          if (metaTown) updateData.town = metaTown;
          if (metaName) updateData.name = metaName;
          if (userMeta.phone) updateData.phone = userMeta.phone;
          if (userMeta.years_of_experience) updateData.years_of_experience = userMeta.years_of_experience;
          if (userMeta.education_level) updateData.education_level = userMeta.education_level;
          if (userMeta.date_of_birth) updateData.date_of_birth = userMeta.date_of_birth;
          if (userMeta.business_name) updateData.business_name = userMeta.business_name;
          if (userMeta.business_type) updateData.business_type = userMeta.business_type;
          if (userMeta.industry) updateData.industry = userMeta.industry;
          if (userMeta.business_description) updateData.business_description = userMeta.business_description;
          if (userMeta.business_phone) updateData.business_phone = userMeta.business_phone;
          if (userMeta.employee_count) updateData.number_of_employees = userMeta.employee_count;
          if (userMeta.skills && Array.isArray(userMeta.skills) && userMeta.skills.length > 0) updateData.skills = userMeta.skills;
          if (userMeta.terms_agreed_at) updateData.terms_agreed_at = userMeta.terms_agreed_at;

          const { error: updateError } = await supabase
            .from('profiles')
            .update(updateData)
            .eq('id', profileData.id);

          if (updateError) {
            console.error('[AuthCallback] Error updating profile with user_metadata:', updateError);
          } else {
            console.log('[AuthCallback] Profile updated with user_metadata');
            // Update local profile object for subsequent checks
            if (profile) {
              Object.assign(profile, updateData);
            }
          }
        }
      }

      // Sync fields the trigger doesn't extract (skills, date_of_birth)
      // This runs for email-verified signups where the trigger created the profile
      // but didn't extract these fields from user_metadata
      if (profile?.id && profile.role && profile.role !== 'pending') {
        const metaSync = {};
        if (userMeta.skills && Array.isArray(userMeta.skills) && userMeta.skills.length > 0 && (!profile.skills || profile.skills.length === 0)) {
          metaSync.skills = userMeta.skills;
        }
        if (userMeta.date_of_birth && !profile.date_of_birth) {
          metaSync.date_of_birth = userMeta.date_of_birth;
        }
        if (Object.keys(metaSync).length > 0) {
          console.log('[AuthCallback] Syncing missing fields from user_metadata:', Object.keys(metaSync));
          metaSync.updated_at = new Date().toISOString();
          const { error: syncError } = await supabase
            .from('profiles')
            .update(metaSync)
            .eq('id', profile.id);
          if (syncError) {
            console.error('[AuthCallback] Error syncing metadata fields:', syncError);
          } else {
            Object.assign(profile, metaSync);
          }
        }
      }

      // Check if user needs to complete profile
      const effectiveRole = profile?.role && profile.role !== 'pending' ? profile.role : metaRole;
      // PESO/NSRP registrants are fully onboarded by the NSRP form — skip role-selection
      // AND profile-completion on the callback path too (write-once registration_source).
      const isPesoRegistrant = Boolean(profile?.registration_source && String(profile.registration_source).startsWith('peso_'));
      const needsRoleSelection = !isPesoRegistrant && (!effectiveRole || effectiveRole === 'pending' || effectiveRole === '');

      // Check if user has completed mandatory profile fields
      const isJobSeeker = effectiveRole === 'job_seeker' || effectiveRole === 'contractor';
      const isEmployer = effectiveRole === 'employer' || effectiveRole === 'owner';

      // Get effective profile data (merged with user_metadata if we just updated)
      const effectivePhone = profile?.phone || userMeta.phone;
      const effectiveYearsExp = profile?.years_of_experience || userMeta.years_of_experience;
      const effectiveEducation = profile?.education_level || userMeta.education_level;
      const effectiveDateOfBirth = profile?.date_of_birth || userMeta.date_of_birth;
      const effectiveBusinessName = profile?.business_name || userMeta.business_name;
      const effectiveIndustry = profile?.industry || userMeta.industry;
      const effectiveEmployeeCount = profile?.number_of_employees || userMeta.employee_count;

      // Profile is incomplete if missing phone OR (job seeker missing experience/education) OR (employer missing required fields)
      const needsProfileCompletion = !isPesoRegistrant && effectiveRole && effectiveRole !== 'pending' && (
        !effectivePhone || effectivePhone.trim() === '' ||
        (isJobSeeker && (!effectiveYearsExp || !effectiveEducation || !effectiveDateOfBirth)) ||
        (isEmployer && (!effectiveBusinessName || !effectiveIndustry || !effectiveEmployeeCount))
      );

      if (isSocialLogin && needsRoleSelection) {
        console.log('[AuthCallback] Social login user needs role selection');
        dispatch({ type: 'SET_STATUS', payload: '✅ Account created! Setting up your profile...' });
        dispatch({ type: 'SET_REDIRECT', payload: '/role-selection', delay: 1000 });
      } else if (needsRoleSelection) {
        dispatch({ type: 'SET_STATUS', payload: '✅ Account verified! Completing setup...' });
        dispatch({ type: 'SET_REDIRECT', payload: '/role-selection', delay: 1000 });
      } else if (needsProfileCompletion) {
        console.log('[AuthCallback] User needs to complete profile, missing mandatory fields');
        dispatch({ type: 'SET_STATUS', payload: '✅ Almost there! Complete your profile...' });
        dispatch({ type: 'SET_REDIRECT', payload: '/complete-profile', delay: 1000 });
      } else {
        console.log('[AuthCallback] User has role:', effectiveRole);
        // A brand-new account landing straight on the dashboard (e.g. the email was just
        // verified): report the sign-up server-side with the same reg-<user id> the pixel
        // used at signup. Old accounts logging in are skipped (created_at check + backend guard).
        if (profile?.created_at) {
          await reportRegistration({
            token: session.access_token,
            userId: session.user.id,
            role: effectiveRole,
            createdAt: profile.created_at,
          });
        }
        // Only here: the onboarding gates above must still win over a stored
        // destination — someone who needs role selection has to do it first.
        const returnTo = takeReturnTo();
        dispatch({ type: 'SET_STATUS', payload: returnTo ? '✅ Success! Taking you back...' : '✅ Success! Redirecting to dashboard...' });
        dispatch({ type: 'SET_REDIRECT', payload: returnTo || '/dashboard', delay: 1000 });
      }
    } catch (err) {
      console.error('[AuthCallback] Error in handleSessionSuccess:', err);
      dispatch({ type: 'SET_STATUS', payload: '✅ Signed in! Redirecting...' });
      dispatch({ type: 'SET_REDIRECT', payload: '/dashboard', delay: 1500 });
    }
  }, []);

  const processAuth = useCallback(async () => {
    try {
      // Check for PKCE flow code in query params (Google OAuth)
      const urlParams = new URLSearchParams(window.location.search);

      // Check for error params from Supabase OAuth errors
      const authError = urlParams.get('error');
      const authErrorCode = urlParams.get('error_code');
      const authErrorDesc = urlParams.get('error_description');

      if (authError || authErrorCode) {
        const desc = authErrorDesc
          ? decodeURIComponent(authErrorDesc.replace(/\+/g, ' '))
          : 'Authentication failed';
        console.error('[AuthCallback] Auth error:', authErrorCode, desc);
        dispatch({ type: 'SET_STATUS', payload: `❌ ${desc}` });
        dispatch({ type: 'SET_REDIRECT', payload: '/sign-up', delay: 3000 });
        return;
      }

      const code = urlParams.get('code');

      // Get the URL hash for OAuth tokens and type detection
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');

      // Check if this is a password recovery flow
      if (type === 'recovery') {
        console.log('[AuthCallback] Password recovery flow detected');
        dispatch({ type: 'SET_STATUS', payload: '🔐 Verifying reset link...' });

        // Exchange the tokens to establish the session
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('[AuthCallback] Error setting recovery session:', error);
            dispatch({ type: 'SET_STATUS', payload: '❌ Invalid or expired reset link' });
            dispatch({ type: 'SET_REDIRECT', payload: '/forgot-password', delay: 2000 });
            return;
          }
        }

        dispatch({ type: 'SET_STATUS', payload: '✅ Verified! Redirecting to reset password...' });
        dispatch({ type: 'SET_REDIRECT', payload: '/reset-password', delay: 1000 });
        return;
      }

      // Check if this is a password recovery PKCE flow (redirectTo includes flow=recovery)
      const flowParam = urlParams.get('flow');

      if (code) {
        console.log('[AuthCallback] PKCE code detected, exchanging for session...');
        dispatch({ type: 'SET_STATUS', payload: '🔐 Completing sign in...' });

        const { data: { session }, error } = await supabase.auth.exchangeCodeForSession(code);

        if (error) {
          console.error('[AuthCallback] Error exchanging code:', error);

          // PKCE code_verifier missing = user opened verification email on a different device/browser
          // Their account IS verified, they just need to log in manually
          if (error.message?.includes('code verifier') || error.message?.includes('code_verifier')) {
            console.log('[AuthCallback] PKCE code_verifier missing - different device/browser');
            dispatch({ type: 'SET_STATUS', payload: '✅ Account verified! Please sign in with your email and password.' });
            dispatch({ type: 'SET_REDIRECT', payload: '/sign-up', delay: 3000 });
            return;
          }

          dispatch({ type: 'SET_STATUS', payload: `❌ Error: ${error.message}` });
          dispatch({ type: 'SET_REDIRECT', payload: '/sign-up', delay: 3000 });
          return;
        }

        // If this PKCE exchange was for password recovery, redirect to reset-password
        if (session && flowParam === 'recovery') {
          console.log('[AuthCallback] PKCE recovery flow - redirecting to reset-password');
          dispatch({ type: 'SET_STATUS', payload: '✅ Verified! Redirecting to reset password...' });
          dispatch({ type: 'SET_REDIRECT', payload: '/reset-password', delay: 1000 });
          return;
        }

        if (session) {
          await handleSessionSuccess(session);
          return;
        }
      }

      // Get tokens for OAuth (implicit flow fallback)
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      // If we have OAuth tokens in hash, exchange them
      if (accessToken && refreshToken) {
        console.log('[AuthCallback] OAuth tokens detected in URL hash');
        dispatch({ type: 'SET_STATUS', payload: '🔐 Completing sign in...' });

        const { data: { session }, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });

        if (error) {
          console.error('[AuthCallback] Error setting session:', error);
          dispatch({ type: 'SET_STATUS', payload: `❌ Error: ${error.message}` });
          dispatch({ type: 'SET_REDIRECT', payload: '/sign-up', delay: 3000 });
          return;
        }

        if (session) {
          await handleSessionSuccess(session);
          return;
        }
      }

      // Fallback: Check for existing session (email verification flow)
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error('Auth callback error:', error);
        dispatch({ type: 'SET_STATUS', payload: `❌ Error: ${error.message}` });
        dispatch({ type: 'SET_REDIRECT', payload: '/sign-up', delay: 3000 });
        return;
      }

      if (session) {
        await handleSessionSuccess(session);
      } else {
        dispatch({ type: 'SET_STATUS', payload: '⏳ No active session found. Redirecting to sign in...' });
        dispatch({ type: 'SET_REDIRECT', payload: '/sign-up', delay: 2000 });
      }
    } catch (err) {
      console.error('Auth callback error:', err);
      dispatch({ type: 'SET_STATUS', payload: `❌ Unexpected error: ${err.message}` });
      dispatch({ type: 'SET_REDIRECT', payload: '/sign-up', delay: 3000 });
    }
  }, [handleSessionSuccess]);

  // Effect to trigger auth processing
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    processAuth();
  }, [processAuth]);

  // CRITICAL: Safety timeout - prevent infinite loading if auth hangs
  useEffect(() => {
    const safetyTimer = setTimeout(() => {
      if (!state.redirect) {
        console.error('[AuthCallback] Safety timeout triggered - auth took too long');
        dispatch({ type: 'SET_STATUS', payload: '⏳ Taking too long... Redirecting to login' });
        dispatch({ type: 'SET_REDIRECT', payload: '/sign-up', delay: 1000 });
      }
    }, SAFETY_TIMEOUT_MS);

    return () => clearTimeout(safetyTimer);
  }, [state.redirect]);

  // Effect to handle redirect after state update
  // CRITICAL: Use window.location.href instead of navigate() to force full page reload
  // This ensures App.js properly initializes with fresh session and profile state
  useEffect(() => {
    if (state.redirect) {
      const timer = setTimeout(() => {
        window.location.href = state.redirect;
      }, state.delay);
      return () => clearTimeout(timer);
    }
  }, [state.redirect, state.delay]);

  return (
    <div className="min-h-screen-safe flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="text-center">
            <div className="mb-4 text-4xl">
              {state.status.includes('✅') ? '✅' : state.status.includes('❌') ? '❌' : '⏳'}
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {state.status.includes('Setting up') || state.status.includes('Completing') 
                ? 'Almost there!' 
                : 'Account Verification'}
            </h2>
            <p className="text-muted-foreground">{state.status}</p>
            
            {/* Loading spinner */}
            {!state.status.includes('❌') && (
              <div className="mt-4 flex justify-center">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthCallback;
